import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
} from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { SortIcon } from 'views/admin/Transactions/components/TransactionsTable';
import { adminService } from 'views/services/adminService';
import { useAdminsStore } from 'views/store/useAdminsStore';
import { TAdminData } from 'views/types/admins';

const columnHelper = createColumnHelper<TAdminData>();

const columns = [
  columnHelper.accessor('login', {
    header: ({ column }) => {
      const sortDirection = column.getIsSorted();

      return (
        <p className="flex items-center gap-2 text-sm font-bold dark:text-white">
          Email
          <SortIcon direction={sortDirection as 'asc' | 'desc'} />
        </p>
      );
    },
    cell: info => info.getValue(),
    enableSorting: true,
  }),
  columnHelper.accessor('role', {
    header: ({ column }) => {
      const sortDirection = column.getIsSorted();

      return (
        <p className="flex items-center gap-2 text-sm font-bold dark:text-white">
          Role
          <SortIcon direction={sortDirection as 'asc' | 'desc'} />
        </p>
      );
    },
    cell: info => info.getValue(),
    enableSorting: true,
  }),
  columnHelper.accessor('name', {
    header: ({ column }) => {
      const sortDirection = column.getIsSorted();

      return (
        <p className="flex items-center gap-2 text-sm font-bold dark:text-white">
          Name
          <SortIcon direction={sortDirection as 'asc' | 'desc'} />
        </p>
      );
    },
    cell: info => info.getValue() || '-',
    enableSorting: true,
  }),
  columnHelper.accessor('telegramId', {
    header: ({ column }) => {
      const sortDirection = column.getIsSorted();

      return (
        <p className="flex items-center gap-2 text-sm font-bold dark:text-white">
          Telegram ID
          <SortIcon direction={sortDirection as 'asc' | 'desc'} />
        </p>
      );
    },
    cell: info => info.getValue() || '-',
    enableSorting: true,
  }),
  columnHelper.accessor('isOnline', {
    header: ({ column }) => {
      const sortDirection = column.getIsSorted();

      return (
        <p className="flex items-center gap-2 text-sm font-bold dark:text-white">
          Online
          <SortIcon direction={sortDirection as 'asc' | 'desc'} />
        </p>
      );
    },
    cell: info => (info.getValue() ? '🟢' : '⚫'),
    enableSorting: true,
  }),
  columnHelper.accessor('isActive', {
    header: ({ column }) => {
      const sortDirection = column.getIsSorted();

      return (
        <p className="flex items-center gap-2 text-sm font-bold dark:text-white">
          Active
          <SortIcon direction={sortDirection as 'asc' | 'desc'} />
        </p>
      );
    },
    cell: info => (info.getValue() ? '✅' : '❌'),
    enableSorting: true,
  }),
  columnHelper.accessor('createdAt', {
    header: ({ column }) => {
      const sortDirection = column.getIsSorted();

      return (
        <p className="flex items-center gap-2 text-sm font-bold dark:text-white">
          Created
          <SortIcon direction={sortDirection as 'asc' | 'desc'} />
        </p>
      );
    },
    cell: info => new Date(info.getValue()).toLocaleString(),
    enableSorting: true,
  }),
];

export const AdminsTable = () => {
  const { admins, setSelectedAdmin, filters } = useAdminsStore();

  const [sorting, setSorting] = useState<SortingState>([]); // from @tanstack/react-table

  const table = useReactTable({
    data: admins,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  useEffect(() => {
    adminService.getAll(undefined, filters);
  }, [filters]);

  console.log({ sorting, filters });

  const handleAdminClick = (admin: TAdminData) => {
    setSelectedAdmin(admin);
  };

  return (
    <div className="p-4">
      <table className="min-w-full border border-gray-200">
        <thead className="bg-gray-100">
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th
                  onClick={header.column.getToggleSortingHandler()}
                  key={header.id}
                  className="dark:bg-gray-800 dark:text-white border border-gray-300 px-4 py-2 text-left"
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="text-center py-8 text-gray-500 dark:text-gray-400"
              >
                No data available
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map(row => (
              <tr
                key={row.id}
                className=" dark:hover:bg-gray-800 hover:bg-gray-50"
                onClick={() => {
                  handleAdminClick(row.original);
                }}
              >
                {row.getVisibleCells().map(cell => (
                  <td
                    key={cell.id}
                    className="cursor-pointer dark:text-white border border-gray-300 px-4 py-2"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};
